import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, Warning, X } from "@phosphor-icons/react";

export function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let cancelled = false;
    const poll = async (attempt = 0) => {
      if (cancelled || attempt >= 12) {
        if (!cancelled) setStatus(attempt >= 12 ? "timeout" : "unknown");
        return;
      }
      setTries(attempt + 1);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (err) {
        console.error("Payment status poll error:", err);
      }
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="billing-success">
        {status === "checking" && (
          <>
            <div className="font-mono text-[#00D4FF] tracking-widest mb-4">[VERIFYING PAYMENT] {tries}/12<span className="cursor-blink">_</span></div>
            <h1 className="font-chivo font-black text-3xl text-white uppercase">Confirming payment...</h1>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={64} weight="fill" className="text-[#00D4FF] mx-auto mb-6" />
            <h1 className="font-chivo font-black text-4xl text-white uppercase mb-4">Membership Active</h1>
            <p className="font-plex text-[#9CA3AF] mb-8">Welcome aboard. Your annual access is now active for 365 days.</p>
            <Link to="/dashboard" className="btn-primary" data-testid="success-dashboard-cta">Go To Dashboard</Link>
          </>
        )}
        {(status === "timeout" || status === "unknown") && (
          <>
            <Warning size={64} weight="fill" className="text-[#FF5722] mx-auto mb-6" />
            <h1 className="font-chivo font-black text-3xl text-white uppercase mb-4">Still Processing</h1>
            <p className="font-plex text-[#9CA3AF] mb-8">Your payment may still be processing. Refresh in a moment or check your account page.</p>
            <Link to="/account" className="btn-primary">Go To Account</Link>
          </>
        )}
        {(status === "expired" || status === "missing") && (
          <>
            <X size={64} weight="bold" className="text-[#EF4444] mx-auto mb-6" />
            <h1 className="font-chivo font-black text-3xl text-white uppercase mb-4">Payment Not Completed</h1>
            <Link to="/billing" className="btn-primary">Try Again</Link>
          </>
        )}
      </div>
    </Layout>
  );
}

export function BillingCancel() {
  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="billing-cancel">
        <X size={64} weight="bold" className="text-[#FF5722] mx-auto mb-6" />
        <h1 className="font-chivo font-black text-4xl text-white uppercase mb-4">Checkout Cancelled</h1>
        <p className="font-plex text-[#9CA3AF] mb-8">No payment was processed. You can retry whenever you're ready.</p>
        <Link to="/billing" className="btn-primary">Return To Billing</Link>
      </div>
    </Layout>
  );
}
